import React, { useRef, useState } from "react";
import { Button, Modal, Header, Form, Input, Message } from "semantic-ui-react";
import Cropper, { ReactCropperElement } from "react-cropper";

import { imageAspectRatio } from "../../../constants/imageUpload";
import * as validationHelper from "../../../helpers/validationHelper";

interface ImageCropperModalProps {
  isOpen: boolean;
  imagePreview: string;
  fileName: string;
  imageType: string;
  width: number;
  height: number;
  uploadType: string;
  showImageUrl?: boolean;
  isUploading?: boolean;
  onConfirm: (croppedBlob: Blob, imageUrl?: string) => void;
  onCancel: () => void;
}

const ImageCropperModal: React.FC<ImageCropperModalProps> = ({
  isOpen,
  imagePreview,
  fileName,
  imageType,
  width,
  height,
  uploadType,
  showImageUrl = false,
  isUploading = false,
  onConfirm,
  onCancel
}) => {
  const cropperRef = useRef<ReactCropperElement>(null);
  const [imageUrl, setImageUrl] = useState<string>("");

  const onCancelClick = (): void => {
    setImageUrl("");
    onCancel();
  };

  const onConfirmClick = (): void => {
    const cropper = cropperRef.current?.cropper;
    if (cropper) {
      cropper.getCroppedCanvas().toBlob((blob: Blob | null) => {
        if (blob) {
          (blob as any).filename = fileName;
          onConfirm(blob, showImageUrl ? imageUrl : undefined);
        }
      }, imageType);
    }
  };

  const onMaximizeCropBoxClick = (): void => {
    const cropper = cropperRef.current?.cropper;
    if (cropper) {
      const maximizeCropData = {
        width: width,
        height: height
      };
      cropper.setCropBoxData(maximizeCropData);
    }
  };

  const onRotateClick = (): void => {
    if (cropperRef.current) {
      cropperRef.current.rotate(90);
    }
  };

  const currentAspectRatio = imageAspectRatio[uploadType];

  const urlValidationModel = {
    url: true
  };
  const isImageUrlValid = validationHelper.isValid(
    urlValidationModel,
    imageUrl
  );
  const imageUrlValidationMessage =
    validationHelper.getValidationErrorMessage(
      urlValidationModel,
      imageUrl
    );

  return (
    <Modal
      open={isOpen}
      size="tiny"
      style={{ width: "auto" }}
    >
      <Modal.Content>
        <Header content="Crop image" textAlign="center" />
        <div
          className="avatar-dropzone-cont cropping-state"
          style={{ textAlign: "-webkit-center" }}
        >
          <div
            className="image-dropzone avatar-dropzone"
            style={{ display: "table-cell" }}
          >
            <div className="logo-overlapping image-cont border-padded">
              <Cropper
                className="logo-box-inner"
                checkCrossOrigin={false}
                ref={cropperRef}
                src={imagePreview}
                aspectRatio={currentAspectRatio}
                guides={false}
                zoomable={false}
                style={{
                  height: `${height}px`,
                  width: `${width}px`
                }}
              />
            </div>
          </div>
        </div>
        {showImageUrl && (
          <Form.Field style={{ marginTop: "10px" }}>
            <label>URL</label>
            <Input
              fluid
              value={imageUrl}
              onChange={(event, { value }) => {
                setImageUrl(value);
              }}
            />
            <Message negative hidden={isImageUrlValid}>
              <p>{imageUrlValidationMessage}</p>
            </Message>
          </Form.Field>
        )}
        <Header as="h2" icon textAlign="center">
          <Header.Content>
            <Button
              style={{ margin: "0px 5px 0px 5px", color: "#fff" }}
              primary
              className="action"
              onClick={onConfirmClick}
              loading={isUploading}
              disabled={!isImageUrlValid || isUploading}
            >
              Confirm
            </Button>
            <Button
              style={{ margin: "0px 5px 0px 5px" }}
              content="Cancel"
              onClick={onCancelClick}
              disabled={isUploading}
            />
            <Button
              style={{ margin: "0px 5px 0px 5px" }}
              className="action"
              content="Expand selection"
              onClick={onMaximizeCropBoxClick}
              disabled={isUploading}
            />
            <Button
              style={{ margin: "0px 5px 0px 5px" }}
              className="action"
              content="Rotate"
              onClick={onRotateClick}
              disabled={isUploading}
            />
          </Header.Content>
        </Header>
      </Modal.Content>
    </Modal>
  );
};

export default ImageCropperModal;
